import { useEffect, useRef } from 'react';

/**
 * useClickOutside — invoke `handler` when a pointer press or Escape key lands
 * outside the referenced element. Ideal for dismissing dropdowns, popovers, and
 * menus. Returns a ref to attach to the container you want to protect.
 *
 * `enabled` lets callers skip the listeners while the menu is closed.
 */
export default function useClickOutside(handler, enabled = true) {
  const ref = useRef(null);

  useEffect(() => {
    if (!enabled) return undefined;

    const onPointer = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        handler(event);
      }
    };
    const onKey = (event) => {
      if (event.key === 'Escape') handler(event);
    };

    document.addEventListener('mousedown', onPointer);
    document.addEventListener('touchstart', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('touchstart', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [handler, enabled]);

  return ref;
}
